import { Users, UserPlus, FileSignature, ArrowRight, Briefcase, GraduationCap } from "lucide-react"
import { Button } from "../../components/ui/button"

export default function RH() {
    return (
        <div className="min-h-screen bg-background font-sans text-foreground">
            {/* Hero Section - Standard (ref: DroitEcoGestion) */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Administration</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Vol. 2024</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Capital</span>
                            <span className="block text-white mt-2">Humain</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Investir dans les talents pour bâtir l'excellence académique.
                                <span className="italic block mt-1 tracking-tight">Notre force, c'est vous.</span>
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                Voir les Offres
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Stats Bar */}
            <div className="border-y border-border bg-secondary/10">
                <div className="container py-4 flex flex-wrap items-center gap-8">
                    {[
                        { val: "Expertise", label: "Recrutement" },
                        { val: "Carrière", label: "Développement" },
                        { val: "Formation", label: "Continue" },
                        { val: "Bien-être", label: "Social" }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-xl font-black text-primary">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</span>
                            {i < 3 && <span className="hidden lg:block text-border ml-4 font-light">/</span>}
                        </div>
                    ))}
                </div>
            </div>

            <article className="container section-padding-sm">
                <div className="grid lg:grid-cols-[250px_1fr] gap-12 lg:gap-20">
                    {/* Sidebar */}
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-6 pb-2 border-b border-border">
                                Sommaire
                            </p>
                            {["Politique", "Services", "Rejoindre"].map((item, i) => (
                                <a key={i} href={`#${item.toLowerCase()}`} className="group flex items-start gap-3 py-2 text-xs font-bold text-muted-foreground hover:text-primary transition-smooth">
                                    <span className="text-accent font-black">0{i + 1}</span>
                                    {item}
                                </a>
                            ))}
                        </nav>
                    </aside>

                    {/* Content */}
                    <main className="space-y-16">
                        {/* Section 1: Politique */}
                        <section id="politique">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Notre Politique RH
                            </h2>
                            <div className="prose-sm max-w-none text-muted-foreground font-medium leading-relaxed">
                                <p>
                                    L'Université de Ngozi s'engage à offrir un environnement de travail stimulant, inclusif et équitable. Nous valorisons la méritocratie, le développement continu des compétences et le bien-être de notre personnel académique et administratif.
                                </p>
                            </div>
                        </section>

                        {/* Section 2: Services */}
                        <section id="services">
                            <h2 className="heading-4 text-foreground mb-8 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Domaines d'Intervention
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                {[
                                    {
                                        t: "Gestion des Carrières",
                                        d: "Accompagnement personnalisé, évaluation des performances et plans d'avancement.",
                                        icon: Briefcase
                                    },
                                    {
                                        t: "Formation Continue",
                                        d: "Programmes de renforcement des capacités pédagogiques et administratives.",
                                        icon: GraduationCap
                                    },
                                    {
                                        t: "Administration du Personnel",
                                        d: "Gestion administrative, paie, contrats et suivi des dossiers individuels.",
                                        icon: Users
                                    },
                                    {
                                        t: "Dialogue Social",
                                        d: "Promotion d'un climat social apaisé et constructif au sein de l'institution.",
                                        icon: FileSignature
                                    }
                                ].map((item, i) => (
                                    <div key={i} className="interactive-card p-6 border-l-4 border-primary transition-smooth">
                                        <item.icon className="w-6 h-6 text-primary mb-3" />
                                        <h3 className="heading-4 mb-2">{item.t}</h3>
                                        <p className="text-xs text-muted-foreground leading-relaxed">{item.d}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Section 3: Join */}
                        <section id="rejoindre" className="bg-secondary/20 p-8 rounded-md border-t-4 border-accent">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                                <div>
                                    <h3 className="heading-4 mb-2">Rejoignez l'Excellence</h3>
                                    <p className="text-xs text-muted-foreground">Consultez nos opportunités de carrière et contribuez à notre mission.</p>
                                </div>
                                <Button className="bg-primary text-primary-foreground px-6 py-3 text-sm font-bold shadow-lg hover:bg-primary/90 transition-smooth rounded-md whitespace-nowrap group">
                                    <UserPlus className="w-4 h-4 mr-2" />
                                    Candidature Spontanée
                                </Button>
                            </div>
                        </section>

                    </main>
                </div>
            </article>
        </div>
    )
}
